import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ProviderInvoiceDocument, ProviderInvoiceStatus } from '@/types'

interface Props {
  invoices: Array<ProviderInvoiceDocument & { id: string }>
  loading: boolean
  onResubmit: (invoice: ProviderInvoiceDocument & { id: string }) => void
}

function fmtDate(ymd: string): string {
  if (!ymd) return ''
  const [Y, M, D] = ymd.split('-').map(Number)
  if (!Y || !M || !D) return ymd
  return new Date(Y, M - 1, D).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function statusBadge(status: ProviderInvoiceStatus) {
  switch (status) {
    case 'submitted':
      return <Badge variant="outline">Submitted</Badge>
    case 'approved':
      return <Badge>Approved</Badge>
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>
    case 'paid':
      return <Badge variant="secondary">Paid</Badge>
    case 'draft':
      return <Badge variant="outline">Draft</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

export function MyInvoicesList({ invoices, loading, onResubmit }: Props) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (invoices.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        You haven't submitted any invoices yet.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Production</TableHead>
            <TableHead className="text-right">Subtotal</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.flatMap((inv) => {
            const rows = [
              <TableRow key={inv.id}>
                <TableCell className="whitespace-nowrap">{fmtDate(inv.invoiceDate)}</TableCell>
                <TableCell className="font-medium">{inv.productionName}</TableCell>
                <TableCell className="text-right">{fmtCurrency(inv.subtotal)}</TableCell>
                <TableCell>{statusBadge(inv.status)}</TableCell>
                <TableCell className="text-right">
                  {inv.status === 'rejected' ? (
                    <Button size="sm" variant="outline" onClick={() => onResubmit(inv)}>
                      Edit &amp; Resubmit
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>,
            ]
            if (inv.status === 'rejected' && inv.rejectionNote) {
              rows.push(
                <TableRow
                  key={`${inv.id}-note`}
                  className="bg-destructive/5 hover:bg-destructive/5"
                >
                  <TableCell colSpan={5} className="text-xs italic text-muted-foreground">
                    Rejection note: {inv.rejectionNote}
                  </TableCell>
                </TableRow>,
              )
            }
            return rows
          })}
        </TableBody>
      </Table>
    </div>
  )
}
